import { memo, useMemo } from 'react'
import { Trophy, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { useRedeemablePositions } from '@/hooks/useRedeemablePositions'
import { useRedeem } from '@/hooks/useRedeem'
import { useSubscription } from '@/hooks/useSubscription'
import { usePmWalletStore } from '@/store/pmWalletStore'
import { cn } from '@/lib/utils'

/**
 * Пилюля «+X.XX 🏆» справа от StreakBar. Manual redeem одним тапом через
 * RelayClient (gasless, client-signed).
 *
 * Показывается только при:
 *   - активная подписка (не expired)
 *   - PM-кошелёк разлочен
 *   - есть redeemable позиции
 *
 * Expired-юзер редиректится на polymarket.com (см. RenewalModal).
 */
export const RedeemPill = memo(function RedeemPill() {
  const { isActive } = useSubscription()
  const pmProxyAddress = usePmWalletStore((s) => s.pmProxyAddress)
  const activeWallet = usePmWalletStore((s) => s.activeWallet)
  const address = activeWallet === 'pm' && isActive ? pmProxyAddress : null
  const { positions } = useRedeemablePositions(address ?? undefined)
  const redeem = useRedeem()

  const total = useMemo(() => {
    return positions.reduce((sum, p) => sum + (p.size || 0), 0)
  }, [positions])

  if (!isActive || positions.length === 0) return null

  const handleClick = () => {
    if (redeem.isPending) return
    redeem.mutate(positions, {
      onSuccess: () => toast.success(`Клейм ${total.toFixed(2)} USDC отправлен`),
    })
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={redeem.isPending}
      aria-label={`Клейм ${total.toFixed(2)} USDC`}
      className={cn(
        'flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold tabular-nums',
        'bg-[#22c55e]/15 text-[#22c55e] hover:bg-[#22c55e]/25',
        'disabled:opacity-60',
        'transition-colors',
      )}
    >
      {redeem.isPending ? (
        <Loader2 className="h-3 w-3 animate-spin" />
      ) : (
        <Trophy className="h-3 w-3" />
      )}
      +{total.toFixed(2)}
    </button>
  )
})
